import type { PaymentProfileOwner } from '@billing/payment-sdk'
import type { CreditBackConfiguration, PaymentProfileSummary } from '~/composables/api/usePayment'
import { refreshAuthToken } from '@camelot/webview-utils'
import { until, useNow } from '@vueuse/core'
import { defineStore } from 'pinia'
import { useCudaApi } from '~/composables/api/useCuda'
import { usePaymentApi } from '~/composables/api/usePayment'

/**
 * How long a fetch waits for native to inject the access token before giving up.
 *
 * Fail-fast deadline, not a coverage window - native answers in ms when it answers at all, and
 * when it doesn't the next push is its 180s refresh cycle, too long to hold a loader for.
 * PaymentProfileHeader re-fetches on remount.
 */
const TOKEN_WAIT_MS = 15000

// Hide the credit-back offer this long before it expires, so users don't book after it ended.
export const CREDIT_BACK_EXPIRY_BUFFER_MS = 15 * 60 * 1000

export function isCreditBackActive(config: CreditBackConfiguration, now: number): boolean {
  const validUntil = config.validUntil ? Date.parse(config.validUntil) : Infinity
  return Date.parse(config.validFrom) <= now && now < validUntil - CREDIT_BACK_EXPIRY_BUFFER_MS
}

export const usePaymentProfileStore = defineStore('payment-profile', () => {
  const appStore = useAppStore()
  const locationStore = useLocationStore()
  const { getAcceptedTnCs } = useCudaApi()
  const { getPaymentProfileSummary, getPaymentProfileOwner, updateBillingAddressDetails, getCreditBack } = usePaymentApi()

  // ---- State
  const acceptedLegalEntitiesTnC = ref<number[] | null>(null)
  const tncLoaded = ref<boolean>(false)
  const paymentProfileError = ref<boolean>(false)
  const paymentProfileLoaded = ref<boolean>(false)
  const paymentProfiles = ref<Array<PaymentProfileSummary>>([])
  const selectedPaymentProfile = ref<PaymentProfileSummary | null>(null)
  const paymentProfileOwner = ref<PaymentProfileOwner | null>(null)
  const creditBack = ref<CreditBackConfiguration | null>(null)
  const now = useNow({ interval: 60_000 })

  // ---- Computed
  // Only for loaded, non-business profiles: there is no credit back for business, so we wait for the profile.
  const activeCreditBack = computed<CreditBackConfiguration | null>(() => {
    const profileType = selectedPaymentProfile.value?.type
    if (!creditBack.value || !profileType || profileType === 'BUSINESS')
      return null
    return isCreditBackActive(creditBack.value, now.value.getTime()) ? creditBack.value : null
  })
  const isItalianCustomer = computed<boolean>(() =>
    paymentProfileOwner.value?.defaultProfile?.address?.countryIsoCode?.toUpperCase() === 'IT',
  )

  const defaultProfileUuid = computed<string | undefined>(() =>
    paymentProfileOwner.value?.defaultProfile?.paymentProfileUuid,
  )

  const defaultAddressUuid = computed<string | undefined>(() =>
    paymentProfileOwner.value?.defaultProfile?.address?.addressUuid,
  )

  const countryCode = computed<string | undefined>(() =>
    paymentProfileOwner.value?.defaultProfile?.address?.countryIsoCode,
  )

  const hasAcceptedTnCForCurrentLegalEntity = computed<boolean>(() => {
    if (acceptedLegalEntitiesTnC.value !== null) {
      return acceptedLegalEntitiesTnC.value.includes(locationStore.city.legalEntityId)
    }
    else {
      return false
    }
  })

  // ---- Actions
  async function loadPaymentProfileOwner(): Promise<void> {
    const result = await getPaymentProfileOwner()
    if (result === null)
      throw new Error('Failed to load payment profile owner')
    paymentProfileOwner.value = result
  }

  async function updateFiscalCode(fiscalCode: string): Promise<boolean> {
    const profileUuid = defaultProfileUuid.value
    const addressUuid = defaultAddressUuid.value
    if (!profileUuid || !addressUuid)
      return false

    const profileId = paymentProfileOwner.value?.defaultProfile?.profileId
    const addressId = paymentProfileOwner.value?.defaultProfile?.address?.addressId
    if (!profileId || !addressId)
      return false

    const result = await updateBillingAddressDetails(profileId, addressId, { fiscalCode })
    return result !== null
  }

  // Shared so back-to-back fetches don't ask native twice or stack two timeouts.
  let tokenWait: Promise<boolean> | null = null

  // False only if the token never turned up - callers surface that instead of staying pending.
  async function waitForAccessToken(): Promise<boolean> {
    if (appStore.accessToken)
      return true

    if (!tokenWait) {
      // Ask ourselves rather than trust app.vue's single boot-time request. No-op outside a
      // webview, idempotent inside one.
      refreshAuthToken(window)

      tokenWait = until(() => !!appStore.accessToken)
        .toBe(true, { timeout: TOKEN_WAIT_MS })
        .finally(() => { tokenWait = null })
    }

    return tokenWait
  }

  async function fetchAcceptedTncs() {
    if (!await waitForAccessToken())
      return

    const response = await getAcceptedTnCs(appStore.accessToken)
    if (response !== null) {
      acceptedLegalEntitiesTnC.value = [...response.body.acceptedLegalEntityTerms]
    }
    else {
      acceptedLegalEntitiesTnC.value = null
    }
    tncLoaded.value = true
  }

  async function fetchPaymentProfiles() {
    if (!await waitForAccessToken()) {
      paymentProfileError.value = true
      return
    }

    const response = await getPaymentProfileSummary(appStore.accessToken)
    if (response === null) {
      paymentProfileError.value = true
      return
    }
    const loadedPaymentProfiles: PaymentProfileSummary[] = response.body.paymentProfiles.map(profile => ({ ...profile }))
    if (loadedPaymentProfiles.length === 1) {
      selectedPaymentProfile.value = loadedPaymentProfiles[0]
    }
    else {
      const defaultProfile = loadedPaymentProfiles.find(profile => profile.defaultProfile)
      if (defaultProfile) {
        selectedPaymentProfile.value = defaultProfile
      }
    }
    paymentProfiles.value = loadedPaymentProfiles
    paymentProfileLoaded.value = true
  }

  function setSelectedPaymentProfile(profile: PaymentProfileSummary) {
    selectedPaymentProfile.value = profile
  }

  async function fetchCreditBack(locationId: number) {
    creditBack.value = null
    if (!await waitForAccessToken())
      return

    const result = await getCreditBack(appStore.accessToken, locationId)
    // Ignore responses for a city the user already switched away from.
    if (locationStore.city.locationId === locationId)
      creditBack.value = result
  }

  return {
    hasAcceptedTnCForCurrentLegalEntity,
    isItalianCustomer,
    defaultProfileUuid,
    defaultAddressUuid,
    countryCode,
    fetchAcceptedTncs,
    fetchPaymentProfiles,
    fetchCreditBack,
    activeCreditBack,
    loadPaymentProfileOwner,
    updateFiscalCode,
    acceptedLegalEntitiesTnC,
    tncLoaded,
    paymentProfileLoaded,
    paymentProfileError,
    paymentProfiles,
    selectedPaymentProfile,
    setSelectedPaymentProfile,
    paymentProfileOwner,
    creditBack,
  }
})
